import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import ServiceGlyph from './ServiceGlyph';
import SectionHeading from './SectionHeading';
import { serviceGroups, serviceMeta } from '../content/services';

/**
 * Огляд послуг на головній.
 *
 * Кожен напрямок має власну сторінку, а тут — короткий огляд із посиланнями.
 * Картка складається з фото, малюнка напрямку (ServiceGlyph) і одного
 * речення про суть: те, що потрібно, аби вибрати, куди йти далі.
 *
 * Фото на картці — те саме, що стоїть банером на сторінці послуги (поле
 * banner у serviceMeta): людина, яка клікнула картку, впізнає кадр.
 * Картці віддається зменшена копія (-sm, 800px), банеру — повна; браузер
 * сам обирає через srcSet.
 *
 * Тло світло-сіре, картки білі: між цим і попереднім варіантом була
 * темно-синя редакція, і контраст був завеликий.
 */
const small = (src) => src.replace(/\.jpg$/, '-sm.jpg');

const Services = () => (
  <section id="services" className="py-16 lg:py-24 bg-slate-50">
    <div className="section-container">
      <motion.div
        className="mb-10 lg:mb-14"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <SectionHeading
          title="Наші послуги"
          caption="Юридичні послуги для медичного бізнесу"
          description="Ми беремо на себе юридичні та бюрократичні аспекти — від реєстрації бізнесу до ліцензії МОЗ, — щоб ви могли зосередитися на лікуванні пацієнтів."
          badge={{ value: '06', label: 'Напрямків' }}
        />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6 items-stretch">
        {serviceGroups.map((group, idx) => {
          const { slug, banner } = serviceMeta[idx];
          return (
            <motion.div
              key={group.title}
              className="h-full"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: Math.min(idx, 3) * 0.07 }}
            >
              <Link
                to={`/poslugy/${slug}`}
                className="group h-full flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-teal-300 hover:shadow-md"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                  {/* alt порожній: фото декоративне, назву дає заголовок нижче. */}
                  <img
                    src={process.env.PUBLIC_URL + small(banner)}
                    srcSet={`${process.env.PUBLIC_URL + small(banner)} 800w, ${process.env.PUBLIC_URL + banner} 1600w`}
                    sizes="(min-width: 1280px) 400px, (min-width: 768px) 50vw, 100vw"
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-3 right-3 rounded-full bg-ink-deep/50 backdrop-blur px-2.5 py-1 text-[0.7rem] font-bold tracking-widest text-white">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>

                <div className="relative flex flex-col flex-1 px-6 pb-6 pt-9 lg:px-7 lg:pb-7">
                  {/* Малюнок напрямку на межі фото й тексту — те саме, що на
                      сторінці послуги й у випадному меню. */}
                  <span className="absolute -top-6 left-6 lg:left-7 w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm text-teal-700 flex items-center justify-center">
                    <ServiceGlyph index={idx} className="w-7 h-7" />
                  </span>

                  <h3 className="text-lg lg:text-xl font-bold text-slate-900 font-display leading-snug group-hover:text-teal-800 transition-colors duration-200">
                    {group.title}
                  </h3>
                  <p className="mt-2 text-slate-600 leading-relaxed line-clamp-3">{group.subtitle}</p>

                  <span className="mt-auto pt-5 inline-flex items-center gap-2 text-teal-700 font-semibold text-sm">
                    Детальніше
                    <Icon name="fa-arrow-right" className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      <p className="mt-10 max-w-3xl text-slate-600 leading-relaxed">
        Не впізнали свого випадку в цьому переліку?{' '}
        <Link
          to="/z-chogo-pochaty"
          className="text-teal-700 font-semibold hover:text-teal-800 transition-colors duration-200"
        >
          Подивіться ситуації, з якими до нас звертаються
        </Link>{' '}
        — вони описані так, як їх формулює клієнт, а не як називається послуга.
      </p>
    </div>
  </section>
);

export default Services;
